import moment from "moment";

import { ConcurrencyTarget } from "../../models/concurrencyTargetEnum";

import type { NetCancel, TEENet } from "../../types/tee";

const TIMEOUT = 0;

export interface ExtraOptions {
  // use provided url
  useExactPath?: boolean;
  // additional headers
  headers?: object;
  // the field to return concurrency version
  concurrencyTarget?: ConcurrencyTarget;
  method?: string;
  // use provided headers
  useExactHeaders?: boolean;
  // do not add any Content-Type
  skipContentTypeHeader?: boolean;
  // the same key for requests
  singletonKey?: string;
}

interface ConcurrencyData {
  ConcurrencyVersion?: string;
  changes?: unknown;
  concurrencyVersion?: string;
}

type TeeFunctionWrapper = (
  path: string,
  data: unknown,
  callback?: (data: unknown, textStatus: string, jqXHR: JQueryXHR) => void,
  errorback?: (jqxhr: JQueryXHR, textStatus: string, error: string) => void,
  useExactPath?: boolean,
  headers?: object,
  method?: string,
) => JQueryXHR;

const handleError = (t: JQueryXHR): never => {
  let errorDetail = "";
  if (t.statusText) {
    if (t.responseText) {
      errorDetail = t.responseText;
    }
  } else {
    errorDetail = t.responseText;
  }
  throw new Error(errorDetail);
};

export const convertToPromise = <T>(
  _pathOrOptionsOrMethod: unknown,
  optionsOrMethod: unknown | (() => JQueryPromise<T>),
  method?: (...args: unknown[]) => JQueryPromise<T>,
): Promise<T> => {
  const promise = new Promise<T>((resolve, reject) => {
    if (typeof optionsOrMethod === "function") {
      optionsOrMethod.call(null, resolve, reject);
    } else if (typeof optionsOrMethod === "object" && typeof method === "function") {
      method.call(null, optionsOrMethod, resolve, reject);
    }
  });
  return promise.catch(handleError);
};

export const convertGetToPromise = <T>(
  path: string,
  options: unknown,
  method: (...args: unknown[]) => JQueryPromise<T>,
): Promise<T> => {
  const promise = new Promise<T>((resolve, reject) => {
    // -> .call(this, path, data, callback, errorback, exactUri?)
    method.call(null, path, options, resolve, reject, true);
  });
  return promise.catch(handleError);
};

export const get = <T>(
  Net: TEENet,
  url: string,
  cancelRequestRef?: NetCancel,
  extraOptions?: ExtraOptions,
): Promise<T> => {
  const getAdapter: TeeFunctionWrapper = (path, data, callback, errorback): JQueryXHR => {
    const promise = Net.get(path, data as any, callback as any, errorback);

    const xhr = promise as unknown as JQueryXHR;
    return xhr;
  };

  return tryUntilTimeout<T>(getAdapter, url, null, cancelRequestRef, extraOptions);
};

export const post = <T>(
  Net: TEENet,
  url: string,
  body: unknown,
  cancelLoadToken?: NetCancel,
  extraOptions?: ExtraOptions,
): Promise<T> => {
  const postAdapter: TeeFunctionWrapper = (path, data, callback, errorback): JQueryXHR => {
    const promise = Net.post(path, data as any, callback as any, errorback);

    const xhr = promise as unknown as JQueryXHR;
    return xhr;
  };

  return tryUntilTimeout<T>(postAdapter, url, body, cancelLoadToken, extraOptions);
};

const tryUntilTimeout = <T>(
  teeFunc: TeeFunctionWrapper,
  url: string,
  body?: unknown,
  _cancelRequestRef?: NetCancel,
  extraOptions?: ExtraOptions,
): Promise<T> => {
  const cancelRequestRef = _cancelRequestRef;
  const useExactPath = extraOptions?.useExactPath;
  const headers = extraOptions?.headers;
  const method = extraOptions?.method;

  const promise = new Promise<T>((resolve, reject) => {
    const startTime = moment();

    const exec = (): void => {
      const teePromise = new Promise<T>((resolveInner, rejectInner) => {
        const req: JQueryXHR =
          body !== undefined ?
            teeFunc(
              url,
              body,
              (v, t, x) => {
                onNetResponse(v as T, t, x, resolveInner, extraOptions);
              },
              rejectInner,
              useExactPath,
              headers,
              method,
            )
          : teeFunc(
              url,
              null,
              (v, t, x) => {
                onNetResponse(v as T, t, x, resolveInner, extraOptions);
              },
              rejectInner,
              useExactPath,
              headers,
            );
        if (cancelRequestRef) {
          cancelRequestRef.cancel = () => {
            if (req.abort) {
              req.abort();
            }
          };
        }
      });

      teePromise.then(resolve).catch((jqxhr: JQueryXHR) => {
        const now = moment();
        const duration = moment.duration(now.diff(startTime));
        if (duration.asSeconds() < TIMEOUT) {
          exec();
        } else {
          reject(jqxhr);
        }
      });
    };

    exec();
  });

  return promise.catch(handleError);
};

const onNetResponse = <T>(
  data: T,
  _textStatus: string,
  xhr: JQueryXHR,
  resolver: (value: T | PromiseLike<T>) => void,
  extraOptions?: ExtraOptions,
): void => {
  const concurrencyTarget = extraOptions?.concurrencyTarget;
  if (concurrencyTarget) {
    checkHeaders(data, xhr, concurrencyTarget);
  }
  resolver(data);
};

const checkHeaders = <T>(data: T, x: JQueryXHR, concurrencyTarget?: ConcurrencyTarget): void => {
  const concurrency = x.getResponseHeader("ETag") || x.getResponseHeader("X-TC-ConcurrencyVersion");
  if (concurrency) {
    switch (concurrencyTarget) {
      case ConcurrencyTarget.Encounter:
        (data as unknown as ConcurrencyData).ConcurrencyVersion = concurrency;
        break;
      case ConcurrencyTarget.Changeset:
        // FIXME: it can't be returned. But this concurrencyTarget is not used
        Object.assign(data as unknown as ConcurrencyData, {
          changes: data,
          concurrencyVersion: concurrency,
        });
        break;
      default:
        break;
    }
  }
};
